import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingsAPI } from '../services/api';
import { Booking } from '../types';
import { MapPin, Navigation, Phone, User, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const DriverMapView: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [otp, setOtp] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [trackingLocation, setTrackingLocation] = useState(false);

  useEffect(() => {
    if (bookingId) {
      fetchBooking();
      startLocationTracking();
    }
    return () => {
      stopLocationTracking();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const fetchBooking = async () => {
    try {
      setLoading(true);
      const response = await bookingsAPI.getBookingById(bookingId!);
      setBooking(response.data.booking);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const startLocationTracking = () => {
    if ('geolocation' in navigator) {
      setTrackingLocation(true);
      const watchId = navigator.geolocation.watchPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setCurrentLocation(newLocation);
          
          // Update backend with new location
          if (bookingId) {
            bookingsAPI.updateDriverLocation(
              bookingId,
              newLocation.lat,
              newLocation.lng
            ).catch(err => console.error('Failed to update location:', err));
          }
        },
        (error) => {
          console.error('Location tracking error:', error);
          setTrackingLocation(false);
        },
        {
          enableHighAccuracy: true,
          maximumAge: 10000,
          timeout: 5000
        }
      );

      // Store watchId for cleanup
      (window as any).locationWatchId = watchId;
    }
  };

  const stopLocationTracking = () => {
    if ((window as any).locationWatchId) {
      navigator.geolocation.clearWatch((window as any).locationWatchId);
      (window as any).locationWatchId = null;
    }
    setTrackingLocation(false);
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      alert('Please enter a valid 6-digit OTP');
      return;
    }

    try {
      setVerifying(true);
      await bookingsAPI.verifyTripOTP(bookingId!, otp);
      alert('Trip started successfully!');
      navigate('/provider-bookings');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Invalid OTP');
    } finally {
      setVerifying(false);
    }
  };

  const openGoogleMaps = useCallback(() => {
    if (booking?.pickupLocation) {
      const { latitude, longitude } = booking.pickupLocation;
      const url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`;
      window.open(url, '_blank');
    }
  }, [booking]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-300">Loading booking details...</p>
        </div>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error</h2>
          <p className="text-gray-600 mb-4">{error || 'Booking not found'}</p>
          <button
            onClick={() => navigate('/provider-bookings')}
            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800"
          >
            Back to Bookings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Page Title */}
        <div className="bg-black text-white rounded-lg px-6 py-6 shadow-lg">
          <h1 className="text-2xl font-bold mb-2">Trip Navigation</h1>
          <p className="text-white mb-2">
            {booking.otpVerified ? 'Trip in Progress' : 'Ready to Start Trip'}
          </p>
          <p className="text-gray-200 text-sm">
            Booking ID: {(booking.bookingId || booking._id || booking.id)?.toString().slice(-8).toUpperCase()}
          </p>
        </div>
        {/* Customer Info */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <User className="h-5 w-5 mr-2 text-black" />
            Customer Details
          </h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-semibold text-gray-900">
                {typeof booking.customerId === 'object' ? booking.customerId.name : 'Customer'}
              </p>
            </div>
            {booking.customer?.phone && (
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <a
                  href={`tel:${booking.customer.phone}`}
                  className="font-semibold text-black hover:text-gray-800 flex items-center"
                >
                  <Phone className="h-4 w-4 mr-2" />
                  {booking.customer.phone}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Trip Route */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
            <MapPin className="h-5 w-5 mr-2 text-black" />
            Trip Route
          </h2>
          <div className="space-y-4">
            {/* Pickup Location */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-700 mb-1">PICKUP</p>
                <p className="text-gray-900">{booking.pickupLocation?.address}</p>
              </div>
            </div>

            <div className="ml-5 border-l-2 border-gray-300 h-8"></div>

            {/* Dropoff Location */}
            <div className="flex items-start">
              <div className="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-red-700 mb-1">DROP-OFF</p>
                <p className="text-gray-900">{booking.dropoffLocation?.address}</p>
              </div>
            </div>

            {booking.totalDistance && (
              <div className="bg-blue-50 rounded-lg p-4 flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Distance</span>
                <span className="text-lg font-bold text-blue-900">{typeof booking.totalDistance === 'number' ? booking.totalDistance.toFixed(2) : booking.totalDistance || '0.00'} km</span>
              </div>
            )}
          </div>

          <button
            onClick={openGoogleMaps}
            className="w-full mt-6 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center font-medium shadow-sm"
          >
            <Navigation className="h-5 w-5 mr-2" />
            Open in Google Maps
          </button>
        </div>

        {/* OTP Verification */}
        {!booking.otpVerified ? (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-black" />
              Start Trip with OTP
            </h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
              <p className="text-sm text-yellow-800">
                <strong>Important:</strong> Ask the customer for the 6-digit OTP to verify and start the trip.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP from Customer
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="w-full px-4 py-3 text-center text-2xl font-bold tracking-widest border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleVerifyOTP}
                disabled={verifying || otp.length !== 6}
                className="w-full px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium shadow-sm flex items-center justify-center"
              >
                {verifying ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-5 w-5 mr-2" />
                    Verify & Start Trip
                  </>
                )}
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-green-50 border border-green-200 rounded-lg p-6">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-green-900 text-center mb-2">Trip Started!</h3>
            <p className="text-green-700 text-center">
              Your location is being tracked and shared with the customer.
            </p>
            <p className="text-sm text-green-600 text-center mt-2">
              Started at: {new Date(booking.tripActualStartDateTime!).toLocaleTimeString()}
            </p>
          </div>
        )}

        {/* Location Tracking Status */}
        {trackingLocation && currentLocation && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-600 rounded-full animate-pulse mr-3"></div>
                <span className="text-sm font-medium text-blue-900">Location tracking active</span>
              </div>
              <span className="text-xs text-blue-600">
                {currentLocation.lat.toFixed(4)}, {currentLocation.lng.toFixed(4)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverMapView;

